import { memo, useCallback, useState } from 'react'

import { Divider } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { useVideoObjectBlobDeleteMutation } from 'hooks/mutations/content'
import { videoObjectBlobRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object/Blobs/Blob'
import { videoObjectQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { videoobjectId: videoId, blobId } = videoObjectBlobRoute.useParams()
  const navigate = useNavigate({ from: videoObjectBlobRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const blobQuery = useQuery(videoObjectQueryKeys.blobDetail({ videoId, blobId }))
  const { data: blob } = blobQuery

  const { mutate: deleteBlob } = useVideoObjectBlobDeleteMutation()

  const handleDeletion = useCallback(() => {
    if (!blob) return

    setAnchorEl(undefined)

    deleteBlob(
      { videoId, blobId },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Workspace.Video.nameDelete', { name: blob.storeId }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: videoObjectBlobRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [blob, blobId, deleteBlob, enqueueSnackbar, navigate, t, videoId])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={blob?.storeId || ''}
        entity={t('entity:content.blob', { count: 1 })}
      >
        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={blobId} />
        <Divider sx={{ my: 1 }} component="li" />
        <MenuItemDelete staticLabel={t('domain:Workspace.Video.deleteBlob', { count: 1 })} onConfirm={handleDeletion} />
      </ActionMenu>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
